import React, { Component } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Modal from 'react-modal';

import { CHECKOUT_STEP_MAP, GIFT_OPTIONS_STEP } from 'constants/melodyCheckoutFlow';

import { getCleanPath } from 'helpers/MelodyCheckoutFlowControl';
import { configurePurchase } from 'store/ducks/checkout/actions';
import { fetchGiftOptions } from 'store/ducks/giftoptions/actions';
import { toUSD } from 'helpers/NumberFormats';
import css from 'styles/containers/checkout/melody/giftOptions.scss';
import modalCSS from 'styles/components/common/modal.scss';

export class GiftOptions extends Component {
  state = {
    lineMessage: '',
    lineMessageWarning: '',
    charMessage: '',
    charMessageWarning: '',
    giftMessage: '',
    hasFormError: false,
    useGiftReceipt: true,
    useGiftBag: false
  }

  componentDidUpdate(prevProps) {
    const {
      fetchGiftOptions,
      giftWrapId,
      lineItemId,
      giftMessage,
      location: { pathname },
      giftOptions: { isLoaded, isLoading }
    } = this.props;
    const { lineItemId: prevLineItemId, giftOptions: { isLoaded: prevIsLoaded } } = prevProps;
    const isJustLoaded = isLoaded && isLoaded !== prevIsLoaded;
    const isDifferentItem = lineItemId && lineItemId !== prevLineItemId;
    const isGiftOptionsStep = getCleanPath(pathname) === CHECKOUT_STEP_MAP[GIFT_OPTIONS_STEP];

    if (!isGiftOptionsStep) {
      return;
    }

    if (!isLoaded && !isLoading) {
      fetchGiftOptions();
    }

    if (isLoaded && isDifferentItem || isJustLoaded) {
      this.setState({ giftMessage, useGiftReceipt: true, useGiftBag: !!giftWrapId });
      this.addValidationMessages(giftMessage);
    }
  }

  onGiftMessageChange = e => {
    const { target: { value: giftMessage } } = e;
    this.setState({ giftMessage });
    this.addValidationMessages(giftMessage);
  }

  onHideModal = () => {
    const { onHideGiftOptionsClick } = this.props;
    this.conditionallyRemoveFormError();
    onHideGiftOptionsClick();
  }

  onSavingGiftOptions = e => {
    e.preventDefault();
    const { configurePurchase, giftOptions: purchaseGiftOptions, lineItemId, melodyCheckoutData } = this.props;
    const { giftOptions, isLoading } = purchaseGiftOptions;
    const { isFetchingCheckoutData, productsByLineItem } = melodyCheckoutData;
    const { giftMessage, useGiftBag, useGiftReceipt } = this.state;

    if (isFetchingCheckoutData || isLoading) {
      return;
    }

    if (useGiftBag && !giftMessage.length) {
      this.setState({ hasFormError: true });
      return;
    }

    const product = productsByLineItem[lineItemId];
    const { quantity, hasGiftReceipt: savedGiftReceipt } = product;
    const giftOption = giftOptions[lineItemId];
    const { giftWrap: [ { giftWrapId, unitPriceOverridden } ] } = giftOption;

    const options = {
      unitPriceOverridden,
      lineItemId,
      giftMessage,
      quantity,
      removeGiftOptions: false,
      giftReceipt: useGiftReceipt
    };

    if (useGiftBag) {
      options.giftWrapId = giftWrapId;
    }

    if (!useGiftReceipt) {
      if (!savedGiftReceipt) {
        // item didn't already have a gift receipt, no reason to call configure to remove it
        this.onHideModal();
        return;
      }
      options.removeGiftOptions = true;
    }

    this.conditionallyRemoveFormError();
    configurePurchase({ giftOptions: [ options ], advanceOnSuccess: true });
  }

  addValidationMessages = giftMessage => {
    const { lineItemId, giftOptions: { isLoaded, giftOptions } } = this.props;

    if (!lineItemId || !isLoaded) {
      return;
    }

    const giftOption = giftOptions[lineItemId];
    const { giftMessage: { maxLines, maxTotalLength } } = giftOption;

    // Count the number of lines
    const numLines = giftMessage.split('\n').length;
    const size = giftMessage.length;

    // Determine the differences
    const charDelta = maxTotalLength - size;
    let lineDelta = maxLines - numLines;

    // Adjusting the line count according to entered text (24 characters = 1 line)
    const tempMsg = giftMessage.split('\n');
    tempMsg.forEach(eachLine => {
      if (eachLine.length > 24) {
        if ((eachLine.length % 24) === 0) {
          lineDelta -= Math.floor(eachLine.length / 24) - 1;
        } else {
          lineDelta -= Math.floor(eachLine.length / 24);
        }
      }
    });

    let charMessage = '';
    let lineMessage = '';
    let charMessageWarning = false;
    let lineMessageWarning = false;

    switch (true) {
      case charDelta < -1:
        charMessage = `${Math.abs(charDelta)} characters over`;
        charMessageWarning = true;
        break;
      case charDelta < 0:
        charMessage = '1 character over';
        charMessageWarning = true;
        break;
      case charDelta === 1:
        charMessage = '1 character';
        break;
      default:
        charMessage = `${charDelta} characters`;
    }

    switch (true) {
      case lineDelta < -1:
        lineMessage = `${Math.abs(lineDelta)} lines over`;
        lineMessageWarning = true;
        break;
      case lineDelta < 0:
        lineMessage = '1 line over';
        lineMessageWarning = true;
        break;
      case lineDelta === 1:
        lineMessage = '1 line';
        break;
      default:
        lineMessage = `${lineDelta} lines`;
    }

    this.setState({ lineMessage, lineMessageWarning, charMessage, charMessageWarning });
  }

  conditionallyRemoveFormError = () => {
    const { hasFormError } = this.state;
    if (hasFormError) {
      this.setState({ hasFormError: false });
    }
  }

  toggleUseGiftBag = () => {
    const { useGiftBag, useGiftReceipt } = this.state;
    const newVal = !useGiftBag;
    this.setState({ useGiftBag: newVal, hasFormError: false });

    if (newVal && !useGiftReceipt) {
      // must have gift receipt if want gift bag
      this.setState({ useGiftReceipt: true });
    }
  }

  toggleUseGiftReceipt = () => {
    const { useGiftReceipt } = this.state;
    const newVal = !useGiftReceipt;
    this.setState({ useGiftReceipt: newVal, hasFormError: false });

    if (!newVal) {
      this.setState({ useGiftBag: false });
    }
  }

  render() {
    const {
      giftOptions: { isLoaded, isLoading: giftOptionsIsLoading, giftOptions },
      melodyCheckoutData: {
        isLoading: purchaseDataIsLoading,
        purchase: { eligibleForGiftWrap },
        productsByLineItem
      },
      lineItemId,
      location: { pathname }
    } = this.props;

    const { testId = f => f, marketplace: { checkout: { allowGiftOptions } } } = this.context;
    const isGiftOptionsStep = getCleanPath(pathname) === CHECKOUT_STEP_MAP[GIFT_OPTIONS_STEP];

    if (!isGiftOptionsStep) {
      return null;
    }

    if (!eligibleForGiftWrap || !allowGiftOptions || !isLoaded || !lineItemId || !productsByLineItem) {
      return null;
    }

    const {
      giftMessage,
      useGiftBag,
      useGiftReceipt,
      hasFormError,
      lineMessage,
      lineMessageWarning,
      charMessage,
      charMessageWarning
    } = this.state;

    const charWrapperClass = cn({ [css.warning]: charMessageWarning });
    const lineWrapperClass = cn({ [css.warning]: lineMessageWarning });
    const giftMessageText = useGiftBag ? 'Printed on a card attached to the gift' : 'Printed on the gift receipt';
    const giftOption = giftOptions[lineItemId];
    const { giftWrap: [ { price: itemPrice } ] } = giftOption;
    const price = itemPrice;
    const product = productsByLineItem[lineItemId];
    const {
      image: { url: imageUrl, alt: imageAlt },
      brandName,
      style
    } = product;

    return (
      <Modal
        className={cn(modalCSS.modalContent, css.modalContent, { [css.fade]: true })}
        bodyOpenClassName={css.fix}
        overlayClassName={modalCSS.modalOverlay}
        isOpen={isGiftOptionsStep}
        shouldCloseOnOverlayClick={false}
        ariaHideApp={false}
        onRequestClose={this.onHideModal}
        contentLabel="giftOptionsModal">

        <div className={css.modal} data-test-id={testId('giftOptionsModal')}>
          <div className={modalCSS.header}>
            <p className={css.header}>
              Choose Gift Options
            </p>
            <button
              type="button"
              onClick={this.onHideModal}
              className={modalCSS.close}
              data-test-id={testId('closeModal')}
              aria-label="Close"/>
          </div>

          {
            !isLoaded || giftOptionsIsLoading
              ? <div className={css.formWrapper}>Gift options are loading...</div>
              : <div className={css.formWrapper}>
                <form
                  className={css.form}
                  action="TBD"
                  method="POST"
                  onSubmit={this.onSavingGiftOptions}>
                  <div>
                    { hasFormError && <div role="alert" className={css.errorMsg}>Please enter a gift message</div> }
                    { !useGiftReceipt && <div role="alert" className={css.noReceipt}>This shipment will not include any receipt and your message will not be saved.</div> }
                  </div>

                  <div className={css.itemRow}>
                    <div><img width="80" src={imageUrl} alt={imageAlt} /></div>
                    <div>
                      <div className={css.itemDetails}>
                        <div>{brandName}</div>
                        <div className={css.itemName}>{style}</div>
                        <div className={css.fieldWrapper}>
                          <div className={css.formField}>
                            <input
                              type="checkbox"
                              name="useGiftReceipt"
                              data-test-id={testId('useGiftReceipt')}
                              id="useGiftReceipt"
                              checked={useGiftReceipt}
                              onChange={this.toggleUseGiftReceipt} />
                            <label htmlFor="useGiftReceipt">Gift Receipt</label>
                          </div>
                        </div>
                        <div className={css.fieldWrapper}>
                          <div className={css.formField}>
                            <input
                              type="checkbox"
                              name="useGiftBag"
                              data-test-id={testId('useGiftBag')}
                              id="useGiftBag"
                              checked={useGiftBag}
                              onChange={this.toggleUseGiftBag} />
                            <label htmlFor="useGiftBag">Gift Bag/Box {makePrice(price)}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className={css.fieldWrapper}>
                      <div className={cn(css.formField, { [css.fieldError]: hasFormError })}>
                        <label htmlFor="giftMessage">Gift Message ({giftMessageText})</label>
                        <textarea
                          className={css.giftMessage}
                          onChange={this.onGiftMessageChange}
                          value={giftMessage || ''}
                          id="giftMessage"
                          name="giftMessage"
                          data-test-id={testId('giftMessage')}
                          rows="4" />
                        <div className={css.messageLineText}>
                          <span className={charWrapperClass}>{charMessage}</span> &#8226; <span className={lineWrapperClass}>{lineMessage}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </form>
              </div>
          }

          <div className={css.footer}>
            <form onSubmit={this.onSavingGiftOptions} action={'tbd'} method="post">
              <button
                type="button"
                onClick={this.onHideModal}
                className={css.cancelBtn}
                data-test-id={testId('cancelBtn')}>Cancel</button>
              <button
                disabled={!isLoaded || giftOptionsIsLoading || purchaseDataIsLoading }
                type="button"
                onClick={this.onSavingGiftOptions}
                className={css.saveBtn}
                data-test-id={testId('saveBtn')}>{purchaseDataIsLoading ? 'Submiting...' : 'Save Gift Options'}</button>
            </form>
          </div>
        </div>
      </Modal>
    );

  }
}

// Currently, gift options will always be free.  We will need to do work in regards to charge summary if they are not free, ie, some states would require tax on the gift option and some wouldn't.
export const makePrice = price => price !== 0 && <span className={css.price}>+{toUSD(price)}</span>;

function mapStateToProps({ giftOptions, melodyCheckoutData, routing }) {
  return {
    giftOptions,
    melodyCheckoutData,
    location: routing.locationBeforeTransitions
  };
}

GiftOptions.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default connect(mapStateToProps, {
  configurePurchase,
  fetchGiftOptions
})(GiftOptions);
